import React, {FC, useEffect} from 'react';

import {sendDesktopAnalyticsPerSession} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {connect} from 'react-redux';

import {GENERAL_EXPERIMENT_INDICATION} from 'constants/analytics/events/general';
import {flags} from 'libs/rollout';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import {CartSubscriptionOfferConnected} from '../../features/cLSubscription/components/cartSubscriptionOffer/cartSubscriptionOffer';
import {CLSubscriptionFeatureConnected} from '../../features/cLSubscription/components/clSubscriptionFeature/cLSubscriptionFeature';
import {VistaPlusSubscriptionConnected} from '../../features/vistaPlus/vistaPlusSubscription/vistaPlusSubscription';
import {getIsReturningCustomerCL} from '../../hooks/returningCustomerCL/utils';
import {CartItemContactsType} from '../../types';
import {BaseCartItem} from '../baseCartItem/baseCartItem';
import {CartItemContactsInfoConnect} from '../cartItemContactsInfo/cartItemContactsInfo';
import {VistaPlusPrescription} from '../vistaPlusPrescription/VistaPlusPrescription';

import s from './cartItemContacts.scss';

export type PropsType = {
    item: CartItemContactsType;
    isReturningUser?: boolean;
};

const VistaPlusContent: FC<PropsType> = ({item}) => {
    return (
        <>
            <VistaPlusPrescription
                boxCount={item.qty}
                contactLensPerBox={item.contactLensPerBox}
                lensType={item.lensType}
                prescription={item.prescription}
            />
            <VistaPlusSubscriptionConnected
                currentSubscriptionPlan={item.subscription?.currentSubscriptionPlan}
                price={item.subscription.relatedProductSkuPrice}
                productId={item.product_id}
                quoteItemId={item.id}
            />
        </>
    );
};

const ContactsContent: FC<PropsType> = ({item}) => {
    return (
        <>
            <CartItemContactsInfoConnect
                contactLensPerBox={item.contactLensPerBox}
                discountedPrice={item.discounted_price}
                id={item.id}
                lensType={item.lensType}
                prescription={item.prescription}
                prescriptionType={item.contactLensPrescriptionType}
                price={item?.price}
                rebateDiscount={item.rebate_discounts}
                sku={item.sku}
                specialPrice={item.specialPrice}
                subscription={item.subscription}
            />
            {item.subscription && (
                <CLSubscriptionFeatureConnected
                    isSubscriptionAvailable={item.subscription?.isSubscriptionAvailable}
                >
                    <CartSubscriptionOfferConnected
                        currentSubscriptionPlan={item.subscription.currentSubscriptionPlan}
                        productId={item.product_id}
                        quoteItemId={item.id}
                        subscriptionMode={item.subscription.subscriptionMode}
                    />
                </CLSubscriptionFeatureConnected>
            )}
        </>
    );
};

/**
 * Cart contact lenses item
 */
export const CartItemContacts: React.FC<PropsType> = ({isReturningUser, item}) => {
    useEffect(() => {
        if (!isReturningUser || item.brand === 'Vista Plus' || item.sku.includes('PLA')) return;
        spawnPriorityTask(() => {
            sendDesktopAnalyticsPerSession({
                event: GENERAL_EXPERIMENT_INDICATION,
                isInteraction: false,
                label: {
                    expName: 'dReturningCustomerCL',
                    label: flags.dReturningCustomerCL.isEnabled(),
                },
            });
        });
    }, [isReturningUser, item.brand, item.sku]);

    return (
        <div className={s.item}>
            <BaseCartItem item={item}>
                {item.brand === 'Vista Plus' ? (
                    <VistaPlusContent item={item} />
                ) : (
                    <ContactsContent item={item} />
                )}
            </BaseCartItem>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = () => ({
    isReturningUser: getIsReturningCustomerCL(),
});

export const CartItemContactsConnected = connect(mapStateToProps)(CartItemContacts);
